<template>
  <v-navigation-drawer v-model="drawer" :rail="rail" permanent @click="rail = false"
                       class="bg-neutral-50 dds-side-nav">
    <div class="rail-toggle-wrapper" :class="rail ? 'justify-center' : 'justify-end'">
      <v-btn
          class="rail-btn"
          elevation="0"
          density="compact"
          :icon="rail ? 'mdi-chevron-right' : 'mdi-chevron-left'"
          variant="text"
          @click.stop="rail = !rail"
          slim
      />
    </div>
    <slot name="prepend" :rail="rail" />
    <slot name="menu" v-if="$slots.menu"/>
    <v-list density="compact" :nav="true" :opened="rail ? [] : opened" v-else>
      <template v-for="(menuItemGroup, groupIndex) in normalizedMenu">
        <v-divider v-if="groupIndex > 0" class="my-1"/>
        <p class="text-caption" v-if="menuItemGroup.caption">{{ menuItemGroup.caption }}</p>
        <template v-for="menuItemGroupLink in menuItemGroup.links">
          <v-list-item v-if="!menuItemGroupLink.childLinks && checkVisibility(menuItemGroupLink)"
                       :prepend-icon="menuItemGroupLink.icon"
                       :active="menuItemGroupLink.to === currentPath"
                       :disabled="menuItemGroupLink.disabled||false"
                       :to="menuItemGroupLink.to" :title="$t('menu.'+menuItemGroupLink.value)"
                       :value="menuItemGroupLink.value">
            <template v-if="menuItemGroupLink.badgeContent" v-slot:append>
              <v-badge :content="menuItemGroupLink.badgeContent" color="info" inline></v-badge>
            </template>
          </v-list-item>
          <v-list-group v-if="menuItemGroupLink.childLinks" :value="menuItemGroupLink.value">
            <template v-slot:activator="{ props }">
              <v-list-item v-if="checkVisibility(menuItemGroupLink)" v-bind="props"
                           :prepend-icon="menuItemGroupLink.icon"
                           :title="$t('menu.'+menuItemGroupLink.value)">
                <template v-if="menuItemGroupLink.badgeContent" v-slot:append>
                  <v-badge :content="menuItemGroupLink.badgeContent" color="info" inline></v-badge>
                </template>
              </v-list-item>
            </template>
            <template v-for="childLink in menuItemGroupLink.childLinks">
              <v-list-item v-if="checkVisibility(childLink)" :prepend-icon="childLink.icon"
                           :active="childLink.to === currentPath" :disabled="childLink.disabled||false"
                           :to="childLink.to" :title="$t('menu.'+childLink.value)"
                           :value="childLink.value">
                <template v-if="childLink.badgeContent" v-slot:append>
                  <v-badge :content="childLink.badgeContent" color="info" :inline="true"></v-badge>
                </template>
              </v-list-item>
            </template>
          </v-list-group>
        </template>
      </template>
    </v-list>

    <template v-slot:append>
      <div v-if="!rail" class="info-footer">
        <v-list>
          <v-divider/>
          <v-list-item class="mb-2 mt-2">
            <template v-slot:title>
              <div class="info-footer--locale">
                <div>
                  <v-icon icon="flags:austria" class="cursor-pointer"
                          :size="locale === 'de' ? 'large' : 'default'"
                          @click="setLocale('de')"
                  ></v-icon>
                  <v-icon icon="flags:usa" class="cursor-pointer"
                          :size="locale === 'en' ? 'large' : 'default'"
                          @click="setLocale('en')"
                  ></v-icon>
                </div>
              </div>
            </template>
          </v-list-item>
          <v-divider/>
          <v-list-item class="mt-2">
            <template v-slot:title>
              Entwickelt von <a
                href="https://intranet.sp.at.kworld.kpmg.com/Services/IT-Services/Seiten/IT-Services.aspx"
                rel="noreferrer"
                target="_blank">KPMG IT Services GmbH</a><br/>
              <span class="cursor-pointer text-primary"
                    @click="$emit('open-changelog')"
              >v{{ appVersion }}</span>
            </template>
          </v-list-item>
        </v-list>
      </div>
    </template>

  </v-navigation-drawer>
</template>

<script lang="ts">
import {computed, defineComponent, type PropType, ref} from 'vue'
import {isArray} from "@vue/shared"
import {useRoute} from "vue-router"
import {KPMGDDSTheme} from "@/styles/dds-vuetify-theme";
import {useLocalStorage} from "@vueuse/core";
import { setAppLocale } from "@/plugins/i18n";


interface IAppVersionHistoryEntry {
  version: string,
  changes: Array<string>,
}

interface IMenuItemGroup {
  caption?: string
  links: IMenuItemGroupLink[],
}

export interface IMenuItemGroupLink {
  icon: string
  title: string,
  value: string,
  to?: string,
  disabled?: boolean,
  badgeContent?: string,
  visibilityCheck?: boolean,
  service?: string,
  childLinks?: IMenuItemGroupLink[]
}

type Menu = IMenuItemGroup | IMenuItemGroup[]

export default defineComponent({
  emits: ['open-changelog'],
  props: {
    activeRouteLink: String,
    appSupportUrl: String,
    appVersion: String,
    appVersionsHistory: Array as PropType<IAppVersionHistoryEntry[]>,
    menu: {
      type: Object as PropType<Menu>,
    },
    open: Array as PropType<string[]>,
    visibilityCheck: Function as PropType<(menu: IMenuItemGroupLink) => boolean>
  },
  setup(props) {
    const locale = useLocalStorage("locale", "de");
    const rail = useLocalStorage("rail", false)

    const route = useRoute()
    const styleIncidentBgColor = KPMGDDSTheme.colors["kpmgError-100"]
    const styleIncidentFgColor = KPMGDDSTheme.colors["kpmgError-900"]
    const styleLinkColor = KPMGDDSTheme.colors["kpmgTertiary-700"]
    const styleTextColor = KPMGDDSTheme.colors["neutral-500"]
    const styleVersionBgColor = KPMGDDSTheme.colors["kpmgPrimary-100"]
    const styleVersionFgColor = KPMGDDSTheme.colors["kpmgPrimary-500"]

    const drawer = ref<boolean>(true)
    const showVersionHistoryDialog = ref<boolean>(false)

    const currentPath = computed<string>(() => {
      return route.path
    })

    const normalizedMenu = computed<IMenuItemGroup[]>(() => {
      if (props.menu === undefined) {
        return []
      }
      return isArray(props.menu) ? props.menu : [props.menu]
    })

    /**
     * DEV NOTE:
     * This intermediate ref and its time-delayed assignment to the original prop are required
     * because of the broken internals of Vuetify and particularly the opened prop of the v-list component.
     * The exact detail are not clear, but assigned the prop, a ref, computed or const directly to this prop,
     * will cause it to either not be opened at all or opened shortly and then be closed.
     * This hack circumventing the Vuetify bug was found experimentally, but why it works is as unclear as the bug itself.
     */
    const opened = ref<string[]>([])
    setTimeout(() => {
      opened.value = props.open ?? []
    }, 1)

    const reportIncident = (): void => {
      window.open(props.appSupportUrl, "_blank")
    }

    const checkVisibility = (item: IMenuItemGroupLink): boolean => {
      if (props.visibilityCheck != undefined && item.visibilityCheck) {
        return props.visibilityCheck(item)
      }
      return true;
    }

    const setLocale = async (lang: 'de' | 'en') => {
      await setAppLocale(lang)
    }

    return {
      // const
      styleIncidentBgColor,
      styleIncidentFgColor,
      styleLinkColor,
      styleTextColor,
      styleVersionBgColor,
      styleVersionFgColor,
      // ref
      drawer,
      rail,
      showVersionHistoryDialog,
      // computed
      currentPath,
      normalizedMenu,
      opened,
      locale,
      // => f
      setLocale,
      reportIncident,
      checkVisibility,
    }
  }
})
</script>

<style lang="scss">
.rail-toggle-wrapper {
  display: flex;
  padding: 8px 8px 4px;
}

.rail-btn {
  margin-right: 0 !important;

  i {
    margin-left: 0;
  }
}

.info-footer {
  color: v-bind(styleTextColor);
  margin-bottom: 0.75rem;

  a, a:active, a:visited {
    color: v-bind(styleLinkColor);
    text-decoration: none;
  }

  .v-divider {
    margin-bottom: 0.3rem;
  }

  .v-list-item {
    min-height: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;

    .v-list-item-title {
      font-size: 0.7rem;
    }

    .v-chip {
      font-size: 0.7rem;
      height: 1.2rem !important;

      &.incident {
        background-color: v-bind(styleIncidentBgColor);
        color: v-bind(styleIncidentFgColor);
        margin-left: 2rem;

        .v-icon {
          color: v-bind(styleIncidentFgColor);
        }
      }

      &.version {
        background-color: v-bind(styleVersionBgColor);
        color: v-bind(styleVersionFgColor);
      }
    }
  }

}
</style>
